import styles from './Product.module.scss';

import React, { FC, useState } from 'react';
import NativeSelect from '@material-ui/core/NativeSelect';
import { ContentType, LinkVariant, ProductModule } from 'api/contentful/contentTypes.cloverdotcom';
import cx from 'classnames';
import { Button } from 'components/Button/Button';
import { MAX_QUANTITY } from 'components/Cart/utils/Util';
import type { IProductModel } from 'components/Cart/models';
import { Grid } from 'components/Grid/Grid';
import { Image } from 'components/Image/Image';
import { ProductModal } from 'components/ProductModal/ProductModal';
import { TextV2 } from 'components/TextV2/TextV2';
import { useCMSFilteredByDcsId } from 'hooks/useCMSByFilters';
import naImage from 'images/na.png';
import { useTranslation } from 'react-i18next';
import { stringFormatForTestAttr } from 'utils/StringFormatForTest';
import { CART_PRODUCT_TEST_ID } from './Product.const';
import { SUBSCRIPTION_STATE } from 'hooks/useSubscriptionsState';
import { getPriceAndTranslationString } from '../utils/getPriceAndTranslationString';
import { usePaymentsToggle } from 'components/Cart/OrderSummary/PaymentsToggle/usePaymentsToggle/usePaymentsToggle';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import { Installments } from 'components/Cart/OrderSummary/types';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface ICartProductProps {
  productData?: {
    product?: IProductModel;
    removeProductById?: (productId: string) => void;
  };
}

const cartNumberList = Array.from({ length: MAX_QUANTITY - 1 }, (_v, i) => i + 1);
const MAX_QUANTITY_STRING = '10+';
const updatedCartNumberList = [...cartNumberList, MAX_QUANTITY];

export const Product: FC<ICartProductProps> = ({ productData }) => {
  const { t } = useTranslation();
  const { updateProductQuantityById } = useCartContext();
  const { isPayMonthly } = usePaymentsToggle();
  const { selectedPurchaseType } = useCheckout();

  const [modalState, toggleModal] = useState(false);
  const [showQuantityDisclaimer, setQuantityDisclaimer] = useState<boolean>(false);

  const { product, removeProductById } = productData || {};
  const { id: productId, imageURL, name, quantity, totalCost } = product || {};
  const entries = useCMSFilteredByDcsId<ProductModule>(ContentType.PRODUCT_MODULE, productId);
  const productDetails = entries && entries.data;

  const setProductQuantity = (event: React.ChangeEvent<HTMLSelectElement>, showDisclaimer = false) => {
    setQuantityDisclaimer(showDisclaimer);
    updateProductQuantityById(productId ? productId : '', Number(event.currentTarget.value));
  };

  const getDisplayOption = (option: number) => {
    if (option === MAX_QUANTITY) {
      return MAX_QUANTITY_STRING;
    }
    return option;
  };

  if (!productData) return null;

  const priceAndTranslationString = getPriceAndTranslationString(
    t,
    isPayMonthly,
    selectedPurchaseType === Installments.SL36
      ? SUBSCRIPTION_STATE.ENABLED_36_MONTH
      : SUBSCRIPTION_STATE.ENABLED_48_MONTH,
    product,
  );

  return (
    <Grid
      item
      xs={12}
      className={styles.cartItemCellContainer}
      data-testid={stringFormatForTestAttr(`cart-item-${product?.name}`)}
    >
      <Grid item md={12} xs={12} className={styles.cartProductCell}>
        <Image
          src={imageURL || naImage}
          className={cx(styles.cartProductCellImage, {
            [styles.cartProductCellImagePointer]: !!productDetails,
          })}
          aria-hidden="true"
          onClick={productDetails ? () => toggleModal(true) : undefined}
        />
        <Grid className={styles.cartProductCellNumberContainer}>
          <Button
            className={styles.cartProductCellButton}
            onClick={productDetails ? () => toggleModal(true) : undefined}
          >
            {name}
          </Button>
          <NativeSelect
            className={styles.cartProductCellNumberList}
            disableUnderline
            inputProps={{ 'aria-label': `${t('cart-page.quantity')}` }}
            defaultValue={quantity}
            value={quantity}
            onChange={event =>
              Number(event.currentTarget.value) === MAX_QUANTITY
                ? setProductQuantity(event, true)
                : setProductQuantity(event, false)
            }
            data-testid={CART_PRODUCT_TEST_ID.quantityDropdown}
          >
            {updatedCartNumberList.map((optionVal, index) => (
              <option value={optionVal} key={index}>
                {getDisplayOption(optionVal)}
              </option>
            ))}
          </NativeSelect>
          {removeProductById && productId && (
            <Button
              variant={LinkVariant.TEXT}
              className={styles.cartProductCellRemove}
              onClick={() => removeProductById(productId)}
            >
              {t('remove')}
            </Button>
          )}
        </Grid>
        <div className={styles.cartProductCellPriceList}>
          <TextV2
            value={priceAndTranslationString}
            className={styles.cartProductCellCost}
            tagName="p"
            variant="body"
          />
          {quantity && quantity > 1 && !isPayMonthly && (
            <TextV2
              value={`${t('cart-page.each-item-cost', { price: totalCost })}`}
              className={styles.cartProductCellEachCost}
              tagName="p"
              variant="body-small"
            />
          )}
        </div>
        {productDetails && (
          <ProductModal
            closeModal={() => toggleModal(bool => !bool)}
            modalOpen={modalState}
            productDetails={productDetails}
          />
        )}
      </Grid>
      {(showQuantityDisclaimer || quantity === MAX_QUANTITY) && (
        <div className={styles.quantityDisclaimer}>
          <TextV2 value={`${t('cart-page.disclaimer')}`} variant="body" isMarkdown tagName="p" />
        </div>
      )}
    </Grid>
  );
};

Product.displayName = 'Product';
